import React, { useEffect, useState } from 'react';
import axios from '@/lib/axios';
import { API_BASE_URL } from '@/config';

import {
  Loader2,
  Download,
  Eye,
  FileText,
  X
} from 'lucide-react';

import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

interface UserDetailModalProps {
  userId: number | null;
  username?: string;
  firstName?: string;
  lastName?: string;
  businessName?: string | null;
  journeyIndex?: string;
  isOpen: boolean;
  timezone: string;
  onClose: () => void;
}

type DetailSection = Record<
  string,
  string | boolean | number | null | undefined
>;

interface VerificationMediaItem {
  id: string;
  media_type: 'photo' | 'video' | 'document';
  verification_type: string | null;
  verification_type_key: string | null;
  status: string | null;
  filename: string | null;
  size_bytes: number | null;
  uploaded_at: string | null;
  created_at: string | null;
}

interface DocPushFileItem {
  key: string;
  label: string;
  exists: boolean;
}

interface DocPushReadinessItem {
  label: string;
  value: string | number | boolean | null | undefined;
  exists: boolean;
}

interface DocPushReadinessGroup {
  title: string;
  icon: string;
  items: DocPushReadinessItem[];
}

interface DocPushReadiness {
  application_status: string | null;
  doc_push_triggered: boolean;
  groups: DocPushReadinessGroup[];
}

interface UserDetailData {
  user?: DetailSection;
  bap_info?: DetailSection & { is_whitespace?: boolean };
  individual_details?: DetailSection;
  business_details?: DetailSection;
  bank_details?: DetailSection;
  bank_verification?: DetailSection;
  loan_details?: DetailSection;
  pan_verification?: DetailSection;
  gst_verification?: DetailSection;
  journey?: DetailSection & { checkpoint_index?: number };
  mandate_info?: DetailSection;
  bre_info?: DetailSection;
  aa_info?: DetailSection;
  verification_media?: VerificationMediaItem[];
  doc_push_files?: DocPushFileItem[];
  doc_push_readiness?: DocPushReadiness;
}

const LABEL_MAP: Record<string, string> = {
  id: 'User ID',
  username: 'Username',
  first_name: 'First Name',
  last_name: 'Last Name',
  is_staff: 'Staff',
  is_superuser: 'Superuser',
  date_joined: 'Date Joined',
  last_updated: 'Last Updated',

  bap_name: 'BAP Name',
  business_name: 'Business Name',
  anonymized_id: 'Anonymized ID',
  journey_url_type: 'Journey Url Type',

  phone_number: 'Phone Number',
  date_of_birth: 'Date of Birth',
  gender: 'Gender',
  individual_pan_number: 'PAN Number',
  communication_address: 'Communication Address',

  account_number: 'Account Number',
  ifsc_code: 'IFSC',

  pan_no: 'PAN No.',
  name: 'Name on PAN',
  category: 'Category',
  status: 'Status',

  gst_no: 'GST No',
  company_name: 'Company Name',
};

function formatValue(
  value: string | boolean | number | null | undefined
): string {
  if (value === true) return 'Yes';
  if (value === false) return 'No';
  if (value === null || value === undefined || value === '') return 'N/A';
  return String(value);
}

function getLabel(key: string) {
  return (
    LABEL_MAP[key] ??
    key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
  );
}

function DataRow({
  label,
  value,
}: {
  label: string;
  value: any;
}) {
  if (value === null || value === undefined || value === '') return null;
  return (
    <div className="flex justify-between items-center px-4 py-2.5 border-t border-gray-200">
      <span className="text-xs text-gray-800">{label}</span>
      <span className="text-xs text-gray-900 font-medium text-right">
        {formatValue(value)}
      </span>
    </div>
  );
}

function formatSize(bytes?: number | null): string {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const STATUS_BADGE: Record<string, string> = {
  Incoming: 'bg-gray-100 text-gray-600 border-gray-300',
  Encrypted: 'bg-blue-50 text-blue-700 border-blue-300',
  Archived: 'bg-green-50 text-green-700 border-green-300',
  Error: 'bg-red-50 text-red-700 border-red-300',
};

function MediaRow({
  label,
  media,
  mediaUrl,
  setPreviewOpen,
  setPreviewUrl,
}: {
  label: string;
  media?: VerificationMediaItem;
  mediaUrl: (id: string, download?: boolean) => string;
  setPreviewOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setPreviewUrl: React.Dispatch<React.SetStateAction<string>>;
}) {
  if (!media) return null;

  const viewUrl = mediaUrl(media.id);
  const downloadUrl = mediaUrl(media.id, true);

  return (
    <div className="flex justify-between items-center px-4 py-2.5 border-t border-gray-200 gap-3">
      <div className="flex items-center gap-3 min-w-0">
        {/* {media.media_type === 'photo' ? (
          <img
            src={viewUrl}
            alt={label}
            className="w-10 h-10 rounded-lg object-cover border border-gray-200 shrink-0 cursor-pointer"
            onClick={() => window.open(viewUrl, '_blank')}
          />
        ) : media.media_type === 'video' ? (
          <video
            src={viewUrl}
            muted
            className="w-10 h-10 rounded-lg object-cover border border-gray-200 shrink-0 cursor-pointer bg-black"
            onClick={() => window.open(viewUrl, '_blank')}
          />
        ) : (
          <FileText className="w-8 h-8 text-gray-400 shrink-0" />
        )} */}
        <FileText className="w-8 h-8 text-gray-400 shrink-0" />


        <div className="min-w-0">
          <div className="text-xs text-gray-800 truncate">{label}</div>
          <div className="flex items-center gap-1.5 mt-0.5">
            {media.status && (
              <Badge
                variant="outline"
                className={`text-[10px] px-1.5 py-0 ${STATUS_BADGE[media.status] ?? ''}`}
              >
                {media.status}
              </Badge>
            )}
            {media.size_bytes ? (
              <span className="text-[10px] text-gray-400">
                {formatSize(media.size_bytes)}
              </span>
            ) : null}
          </div>
        </div>
      </div>

      <div className="flex gap-2 shrink-0">
        <Button
          variant="outline"
          size="sm"
          className="rounded-xl h-9"
          onClick={() => {
            setPreviewUrl(viewUrl);
            setPreviewOpen(true);
          }}
        >
          <Eye className="w-3.5 h-3.5 mr-1" /> View
        </Button>

        <Button
          variant="outline"
          size="sm"
          className="rounded-xl h-9"
          onClick={() => {
            const a = document.createElement('a');
            a.href = downloadUrl;
            a.download = label;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
          }}
        >
          <Download className="w-3.5 h-3.5 mr-1" /> Download
        </Button>
      </div>
    </div>
  );
}

function Section({
  title,
  colorClass,
  badge,
  children,
}: {
  title: string;
  colorClass: string;
  badge?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="mx-4 mb-4 rounded-xl border border-gray-200 overflow-hidden bg-white">
      <div className={`px-4 py-3 text-sm font-bold uppercase flex items-center justify-between ${colorClass}`}>
        <span>{title}</span>
        {badge && (
          <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-white/60 text-gray-600 normal-case">
            {badge}
          </span>
        )}
      </div>
      {children}
    </div>
  );
}

export const UserDetailModal: React.FC<UserDetailModalProps> = ({
  userId,
  username,
  firstName,
  lastName,
  businessName,
  journeyIndex,
  isOpen,
  timezone,
  onClose,
}) => {
  const [data, setData] = useState<UserDetailData | null>(null);
  const [loading, setLoading] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  const toggleGroup = (title: string) => {
    setExpandedGroups(prev => {
      const next = new Set(prev);
      if (next.has(title)) next.delete(title);
      else next.add(title);
      return next;
    });
  };

  const formatReadinessValue = (value: string | number | boolean | null | undefined): string => {
    if (value === null || value === undefined || value === '') return '—';
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (typeof value === 'number') return String(value);
    // Try to parse JSON strings (panUserNameMatch, AadharSelfieMatchResponse etc.)
    if (typeof value === 'string') {
      try {
        const parsed = JSON.parse(value);
        if (typeof parsed === 'object' && parsed !== null) {
          // Flatten to readable string
          if (parsed.Result) return `Score: ${parsed.Result.Score} — ${parsed.Result.Comment}`;
          if (parsed.confidence !== undefined) return `Confidence: ${parsed.confidence}, Result: ${parsed.match_result}`;
          return JSON.stringify(parsed, null, 0).slice(0, 120);
        }
        return String(parsed);
      } catch {
        // Not JSON — truncate long URLs
        if (value.startsWith('https://') && value.length > 60) return value.slice(0, 55) + '…';
        return value;
      }
    }
    return String(value);
  };

  useEffect(() => {
    if (!isOpen || !userId) return;

    setLoading(true);
    setError(null);
    setData(null);

    axios
      .post(`${API_BASE_URL}/api/public/user_detail/`, {
        user_id: userId,
        timezone,
      })
      .then((res) => setData(res.data))
      .catch(() => setError('Failed to load user details'))
      .finally(() => setLoading(false));
  }, [isOpen, userId, timezone]);

  const isWhitespace = !!data?.bap_info?.is_whitespace;

  const mediaList = data?.verification_media ?? [];
  const findMedia = (keys: string[]) =>
    mediaList.find((m) => m.verification_type_key && keys.includes(m.verification_type_key));
  const mediaUrl = (id: string, download = false) =>
    `${API_BASE_URL}/api/public/media_file/${id}/${download ? '?download=1' : ''}`;
  const selfieMedia = findMedia(['selfie_photo_and_location']);

  const hasBapInfo = !!(
    data?.bap_info?.bap_name ||
    data?.bap_info?.business_name ||
    (data?.bap_info as any)?.journey_url_type ||
    data?.bap_info?.anonymized_id
  );
  const hasPersonalDetails = !!(
    data?.individual_details?.phone_number ||
    data?.individual_details?.date_of_birth ||
    data?.individual_details?.gender ||
    data?.individual_details?.individual_pan_number ||
    data?.individual_details?.communication_address ||
    findMedia(['digio_aadhar']) ||
    findMedia(['digio_pan', 'pan_verification_file'])
  );
  const hasBusinessMedia = !!(
    findMedia(['shop_selfie_photo_and_location']) ||
    findMedia(['shop_only_photo_and_location']) ||
    findMedia(['shop_video_and_location'])
  );
  const hasLoanMedia = !!(
    findMedia(['kfs_final_document']) ||
    findMedia(['signed_loan_agreement']) ||
    findMedia(['cibil_file'])
  );
  const hasBankDetails = !!(
    data?.bank_details?.account_number || data?.bank_details?.ifsc_code
  );
  const hasPanVerification = !!(
    data?.pan_verification?.pan_no ||
    data?.pan_verification?.name ||
    data?.pan_verification?.category ||
    data?.pan_verification?.status
  );
  const hasGstVerification = !!(
    (data as any)?.gst_verification?.gst_no ||
    (data as any)?.gst_verification?.company_name
  );
  const hasVerificationStatus = hasPanVerification || hasGstVerification;
  const hasBankVerificationMedia = !!(
    findMedia(['upi_file']) || findMedia(['bank_verification_file'])
  );

  // ── Progressive stage gating ─────────────────────────────────────────────
  // journeyIndex is "currentPage/totalPages" e.g. "4/19".
  // checkpoint_index from the API is 0-based; page = checkpoint_index + 1.
  // We use the checkpoint_index from the fetched data as the primary signal
  // (more reliable than the prop which may be stale), falling back to the prop.
  const currentPage: number = (() => {
    const chkIdx = data?.journey?.checkpoint_index;
    if (typeof chkIdx === 'number') return chkIdx + 1;
    if (journeyIndex) {
      const parts = journeyIndex.split('/');
      const p = parseInt(parts[0], 10);
      if (!isNaN(p)) return p;
    }
    return 0; // unknown — show everything
  })();

  // Stage thresholds (based on CHECKPOINT_STAGE_MAP in apis.py):
  //   1  Personal Info (pre-BRE)
  //   2  Business Info (pre-BRE)
  //   5  Bureau Verification (BRE1)
  //   6  AA Consent (BRE2)
  //   8  Customize Loan (post-BRE start)
  //  13  Bank Details
  //  14  Digio eMandate Setup (UPI/NACH)
  //  17  Agreement Signing
  //  19  Loan Agreement Status / Completed
  const showBusiness   = currentPage === 0 || currentPage >= 2;
  const showBRE        = currentPage === 0 || currentPage >= 5;
  const showLoanInfo   = currentPage === 0 || currentPage >= 8;
  const showBankInfo   = currentPage === 0 || currentPage >= 13;
  const showMandate    = currentPage === 0 || currentPage >= 14;
  const showDocPush    = currentPage === 0 || currentPage >= 17;

  const handleDownloadPDF = () => {
    if (!data) return;

    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();

    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');

    const displayName =
      firstName || lastName
        ? `${firstName ?? ''} ${lastName ?? ''}`.trim()
        : username ?? `#${userId}`;

    doc.text(`User Details – ${displayName}`, 14, 16);

    let currentHeaderY = 22;

    if (businessName) {
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(100);
      doc.text(businessName, 14, currentHeaderY);
      currentHeaderY += 6;
    }

    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(120);
    doc.text(`Generated: ${new Date().toLocaleString()}`, 14, currentHeaderY);
    doc.setTextColor(0);

    const sections: { title: string; sectionData?: DetailSection }[] = [
      { title: 'Account Info', sectionData: data.user },
      {
        title: 'BAP / Lead Info',
        sectionData: data.bap_info
          ? Object.fromEntries(
              Object.entries(data.bap_info).filter(
                ([k]) => k !== 'is_whitespace'
              )
            )
          : undefined,
      },
      { title: 'Personal Details', sectionData: data.individual_details },
      { title: 'Business Details', sectionData: data.business_details },
      { title: 'Loan Details', sectionData: data.loan_details },
      { title: 'Bank Details', sectionData: data.bank_details },
      { title: 'Bank Verification', sectionData: data.bank_verification },
      { title: 'PAN Verification', sectionData: data.pan_verification },
      { title: 'GST Verification', sectionData: data.gst_verification },
    ];

    let currentY = currentHeaderY + 6;

    sections.forEach(({ title, sectionData }) => {
      if (!sectionData || Object.keys(sectionData).length === 0) return;

      const rows: [string, string][] = Object.entries(sectionData)
        .filter(([, val]) => val !== null && val !== undefined && val !== '')
        .map(([key, val]) => {
          const label = getLabel(key);
          const rawValue =
            val === true ? 'Yes' : val === false ? 'No' : String(val);

          const value = rawValue.replace(/₹/g, 'Rs.');
          return [label, value];
        });

      if (rows.length === 0) return;

      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(60, 60, 60);
      doc.text(title.toUpperCase(), 14, currentY);
      doc.setTextColor(0);
      currentY += 2;

      autoTable(doc, {
        startY: currentY,
        head: [['Field', 'Value']],
        body: rows,
        theme: 'grid',
        styles: {
          fontSize: 8,
          cellPadding: 2.5,
          lineColor: [220, 220, 220],
          lineWidth: 0.1,
        },
        headStyles: {
          fillColor: [240, 240, 240],
          textColor: [40, 40, 40],
          fontStyle: 'bold',
          lineColor: [200, 200, 200],
        },
        columnStyles: {
          0: {
            cellWidth: 55,
            fontStyle: 'bold',
            textColor: [70, 70, 70],
          },
          1: {
            cellWidth: pageWidth - 55 - 28,
          },
        },
        margin: { left: 14, right: 14 },
      });

      currentY =
        (doc as jsPDF & { lastAutoTable: { finalY: number } }).lastAutoTable
          .finalY + 6;
    });

    doc.save(`user_${userId}_details.pdf`);
  };

  const handleDownloadCSV = () => {
    if (!data) return;

    const sections: { title: string; sectionData?: DetailSection }[] = [
      { title: 'Account Info', sectionData: data.user },
      {
        title: 'BAP / Lead Info',
        sectionData: data.bap_info
          ? Object.fromEntries(
              Object.entries(data.bap_info).filter(
                ([k]) => k !== 'is_whitespace'
              )
            )
          : undefined,
      },
      { title: 'Personal Details', sectionData: data.individual_details },
      { title: 'Business Details', sectionData: data.business_details },
      { title: 'Loan Details', sectionData: data.loan_details },
      { title: 'Bank Details', sectionData: data.bank_details },
      { title: 'Bank Verification', sectionData: data.bank_verification },
      { title: 'PAN Verification', sectionData: data.pan_verification },
      { title: 'GST Verification', sectionData: data.gst_verification },
    ];

    const csvRows: string[] = ['Section,Field,Value'];

    sections.forEach(({ title, sectionData }) => {
      if (!sectionData) return;

      Object.entries(sectionData)
        .filter(([, val]) => val !== null && val !== undefined && val !== '')
        .forEach(([key, val]) => {
          const label = getLabel(key);
          const value =
            val === true ? 'Yes' : val === false ? 'No' : String(val);

          const escape = (s: string) => `"${s.replace(/"/g, '""')}"`;

          csvRows.push(
            [escape(title), escape(label), escape(value)].join(',')
          );
        });
    });

    const blob = new Blob([csvRows.join('\n')], {
      type: 'text/csv;charset=utf-8;',
    });

    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `user_${userId}_details.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent className="max-w-5xl w-[95vw] sm:w-[85vw] h-[90vh] p-0 rounded-2xl overflow-hidden bg-[#f6f7fb] flex flex-col">
              <div className="bg-[#fafafa] border-b border-gray-200 px-6 py-5">
          <div className="relative">
<div className="flex flex-col sm:flex-row gap-4 pr-10">
  <img
    src={selfieMedia ? mediaUrl(selfieMedia.id) : '/placeholder.svg'}
    onClick={() => selfieMedia && window.open(mediaUrl(selfieMedia.id), '_blank')}
    className={`w-14 h-14 rounded-full bg-gray-300 object-cover shrink-0 ${selfieMedia ? 'cursor-pointer' : ''}`}
  />

  <div className="min-w-0 flex-1">
    <div className="text-lg sm:text-xl font-bold text-gray-900 leading-tight">
      {(firstName || lastName)
        ? `${firstName ?? ''} ${lastName ?? ''}`.trim()
        : username ?? `User #${userId}`}
    </div>

    {businessName && (
      <div className="text-gray-500 text-sm sm:text-base mt-1 break-words">
        {businessName}
      </div>
    )}

    <div className="flex flex-col gap-2 mt-3">
      <div className="flex flex-wrap items-center gap-2">
        {isWhitespace ? (
          <Badge className="w-fit bg-orange-50 text-orange-700 border border-orange-300 px-3 py-1 rounded-full text-xs">
            Whitespace Journey
          </Badge>
        ) : (
          <Badge className="w-fit bg-green-50 text-green-700 border border-green-300 px-3 py-1 rounded-full text-xs">
            Full Journey
          </Badge>
        )}

        {data?.journey?.current_stage && (
          <span className="text-xs text-gray-600 font-medium">
            {String(data.journey.current_stage)}
          </span>
        )}

        {currentPage > 0 && (() => {
          const totalPages = (() => {
            if (journeyIndex) {
              const parts = journeyIndex.split('/');
              const t = parseInt(parts[1], 10);
              if (!isNaN(t) && t > 0) return t;
            }
            return 19;
          })();
          const pct = Math.min(100, Math.round((currentPage / totalPages) * 100));
          return (
            <span className="text-xs text-gray-400 font-mono">
              Page {currentPage}/{totalPages}
            </span>
          );
        })()}
      </div>

      {/* Stage progress bar */}
      {currentPage > 0 && (() => {
        const totalPages = (() => {
          if (journeyIndex) {
            const parts = journeyIndex.split('/');
            const t = parseInt(parts[1], 10);
            if (!isNaN(t) && t > 0) return t;
          }
          return 19;
        })();
        const pct = Math.min(100, Math.round((currentPage / totalPages) * 100));
        const barColor = currentPage >= 17 ? 'bg-emerald-500' : currentPage >= 8 ? 'bg-blue-500' : 'bg-amber-400';
        return (
          <div className="w-full max-w-xs">
            <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all ${barColor}`}
                style={{ width: `${pct}%` }}
              />
            </div>
            <div className="flex justify-between text-[10px] text-gray-400 mt-0.5">
              <span>Start</span>
              <span className={`font-semibold ${currentPage >= 17 ? 'text-emerald-600' : currentPage >= 8 ? 'text-blue-600' : 'text-amber-600'}`}>
                {pct}%
              </span>
              <span>Done</span>
            </div>
          </div>
        );
      })()}
    </div>

    <div className="flex gap-3 mt-4">
      <Button
        variant="outline"
        className="h-9 px-4 text-sm rounded-lg"
        onClick={handleDownloadPDF}
      >
        PDF
      </Button>

      <Button
        variant="outline"
        className="h-9 px-4 text-sm rounded-lg"
        onClick={handleDownloadCSV}
      >
        CSV
      </Button>
    </div>
  </div>
</div>

          </div>
        </div>

                <div className="overflow-y-auto flex-1 py-6 bg-[#f6f7fb]">
          {loading && (
            <div className="flex justify-center items-center h-full">
              <Loader2 className="w-12 h-12 animate-spin text-blue-500" />
            </div>
          )}

          {error && (
            <div className="text-center text-red-500 py-20">
              {error}
            </div>
          )}
                    {!loading && !error && data && (
            <>
              {/* ACCOUNT INFO */}
              <Section
                title="ACCOUNT INFO"
                colorClass="bg-indigo-50 text-indigo-700"
              >
                <DataRow label="User ID" value={data.user?.id} />
                <DataRow label="Username" value={data.user?.username} />
                <DataRow label="First Name" value={data.user?.first_name} />
                <DataRow label="Staff" value={data.user?.is_staff} />
                <DataRow label="Superuser" value={data.user?.is_superuser} />
                <DataRow label="Date Joined" value={data.user?.date_joined} />
                <DataRow label="Last Updated" value={data.user?.last_updated} />
              </Section>

              {/* BAP */}
              {hasBapInfo && (
                <Section
                  title="BAP / LEAD INFO"
                  colorClass="bg-teal-50 text-teal-700"
                >
                  <DataRow label="BAP Name" value={data.bap_info?.bap_name} />
                  <DataRow
                    label="Business Name"
                    value={data.bap_info?.business_name}
                  />
                  <DataRow
                    label="Journey Url Type"
                    value={(data.bap_info as any)?.journey_url_type}
                  />
                  <DataRow
                    label="Anonymized ID"
                    value={data.bap_info?.anonymized_id}
                  />
                </Section>
              )}

              {/* PERSONAL */}
              {hasPersonalDetails && (
                <Section
                  title="PERSONAL DETAILS"
                  colorClass="bg-blue-50 text-blue-700"
                  badge={currentPage > 0 ? `Page ${currentPage}` : undefined}
                >
                  <DataRow
                    label="Phone Number"
                    value={data.individual_details?.phone_number}
                  />
                  <DataRow
                    label="Date of Birth"
                    value={data.individual_details?.date_of_birth}
                  />
                  <DataRow
                    label="Gender"
                    value={data.individual_details?.gender}
                  />
                  <DataRow
                    label="PAN Number"
                    value={data.individual_details?.individual_pan_number}
                  />

                  <MediaRow
                    label="Aadhaar"
                    media={findMedia(['digio_aadhar'])}
                    mediaUrl={mediaUrl}
                    setPreviewOpen={setPreviewOpen}
                    setPreviewUrl={setPreviewUrl}
                  />

                  <MediaRow
                    label="PAN Document"
                    media={findMedia(['digio_pan', 'pan_verification_file'])}
                    mediaUrl={mediaUrl}
                    setPreviewOpen={setPreviewOpen}
                    setPreviewUrl={setPreviewUrl}
                  />

                  <DataRow
                    label="Communication Address"
                    value={data.individual_details?.communication_address}
                  />
                </Section>
              )}

              {/* BUSINESS */}
              {showBusiness && hasBusinessMedia && (
                <Section
                  title="BUSINESS DETAILS"
                  colorClass="bg-purple-50 text-purple-700"
                >
                  <MediaRow
                    label="Shop Selfie"
                    media={findMedia(['shop_selfie_photo_and_location'])}
                    mediaUrl={mediaUrl}
                    setPreviewOpen={setPreviewOpen}
                    setPreviewUrl={setPreviewUrl}
                  />

                  <MediaRow
                    label="Shop Photo"
                    media={findMedia(['shop_only_photo_and_location'])}
                    mediaUrl={mediaUrl}
                    setPreviewOpen={setPreviewOpen}
                    setPreviewUrl={setPreviewUrl}
                  />

                  <MediaRow
                    label="Shop Video"
                    media={findMedia(['shop_video_and_location'])}
                    mediaUrl={mediaUrl}
                    setPreviewOpen={setPreviewOpen}
                    setPreviewUrl={setPreviewUrl}
                  />
                </Section>
              )}

              {/* BRE / CAPRION STATUS */}
              {showBRE && data?.bre_info && (
                <Section
                  title="BRE / LENDER STATUS"
                  colorClass="bg-orange-50 text-orange-700"
                >
                  <DataRow label="Application ID" value={data.bre_info?.application_id} />
                  <DataRow label="BRE Status" value={data.bre_info?.status} />
                  <DataRow label="Approved Loan Amount" value={data.bre_info?.approved_loan_amount} />
                  <DataRow label="Last Updated" value={data.bre_info?.created_at} />
                </Section>
              )}

              {/* AA SESSION */}
              {showBRE && data?.aa_info && (
                <Section
                  title="AA (ACCOUNT AGGREGATOR)"
                  colorClass="bg-cyan-50 text-cyan-700"
                >
                  <DataRow label="AA Status" value={data.aa_info?.status} />
                  <DataRow label="Initiated At" value={data.aa_info?.created_at} />
                  <DataRow label="Last Updated" value={data.aa_info?.updated_at} />
                </Section>
              )}

              {/* LOAN */}
              {showLoanInfo && hasLoanMedia && (
                <Section
                  title="LOAN DETAILS"
                  colorClass="bg-rose-50 text-rose-700"
                >
                  <MediaRow
                    label="KFS Document"
                    media={findMedia(['kfs_final_document'])}
                    mediaUrl={mediaUrl}
                    setPreviewOpen={setPreviewOpen}
                    setPreviewUrl={setPreviewUrl}
                  />

                  <MediaRow
                    label="Loan Agreement"
                    media={findMedia(['signed_loan_agreement'])}
                    mediaUrl={mediaUrl}
                    setPreviewOpen={setPreviewOpen}
                    setPreviewUrl={setPreviewUrl}
                  />

                  <MediaRow
                    label="CIBIL Report"
                    media={findMedia(['cibil_file'])}
                    mediaUrl={mediaUrl}
                    setPreviewOpen={setPreviewOpen}
                    setPreviewUrl={setPreviewUrl}
                  />
                </Section>
              )}

              {/* LOAN DETAILS (data) */}
              {showLoanInfo && data?.loan_details && (
                <Section
                  title="LOAN OFFER DETAILS"
                  colorClass="bg-pink-50 text-pink-700"
                >
                  <DataRow label="Loan Amount" value={data.loan_details?.loan_amount} />
                  <DataRow label="Approved Amount" value={data.bre_info?.approved_loan_amount} />
                  <DataRow label="Tenure" value={data.loan_details?.loan_tenure} />
                  <DataRow label="Interest Rate" value={data.loan_details?.interest_rate} />
                  <DataRow label="Fee" value={data.loan_details?.fee_amount} />
                  <DataRow label="Fee GST" value={data.loan_details?.fee_gst_amount} />
                  <DataRow label="Repayment Value" value={data.loan_details?.repayment_value} />
                  <DataRow label="Repayment Method" value={data.loan_details?.repayment_method} />
                  <DataRow label="Lender" value={data.loan_details?.lending_entity} />
                </Section>
              )}

              {/* BANK */}
              {showBankInfo && !isWhitespace && hasBankDetails && (
                <Section
                  title="BANK DETAILS"
                  colorClass="bg-green-50 text-green-700"
                >
                  <DataRow
                    label="Account Number"
                    value={data.bank_details?.account_number}
                  />
                  <DataRow
                    label="IFSC"
                    value={data.bank_details?.ifsc_code}
                  />
                </Section>
              )}
              {/* MANDATE INFO */}
              {showMandate && data?.mandate_info && (
                <Section
                  title="UPI / NACH MANDATE"
                  colorClass="bg-amber-50 text-amber-700"
                >
                  <DataRow label="Auth Type" value={data.mandate_info?.auth_type} />
                  <DataRow label="Status" value={data.mandate_info?.status} />
                  <DataRow label="Mandate ID" value={data.mandate_info?.mandate_id} />
                  <DataRow label="UMRN" value={data.mandate_info?.umrn} />
                  <DataRow label="Registered At" value={data.mandate_info?.created_at} />
                </Section>
              )}

              {/* VERIFICATION STATUS */}
              {hasVerificationStatus && (
                <Section
                  title="VERIFICATION STATUS"
                  colorClass="bg-slate-50 text-slate-700"
                >
                  {/* PAN Verification Card */}
                  {hasPanVerification && (
                    <div className="m-5 rounded-2xl border border-gray-200 overflow-hidden bg-white">
                      <div className="px-4 py-2.5 bg-gray-50 font-bold text-[16px]">
                        PAN Verification
                      </div>

                      <DataRow
                        label="PAN No."
                        value={data.pan_verification?.pan_no}
                      />
                      <DataRow
                        label="Name on PAN"
                        value={data.pan_verification?.name}
                      />
                      <DataRow
                        label="Category"
                        value={data.pan_verification?.category}
                      />
                      <DataRow
                        label="Status"
                        value={data.pan_verification?.status}
                      />
                    </div>
                  )}

                  {/* GST Verification Card */}
                  {hasGstVerification && (
                    <div className="mx-5 mb-5 rounded-2xl border border-gray-200 overflow-hidden bg-white">
                      <div className="px-4 py-2.5 bg-gray-50 font-bold text-[16px]">
                        GST Verification
                      </div>

                      <DataRow
                        label="GST No"
                        value={(data as any)?.gst_verification?.gst_no}
                      />
                      <DataRow
                        label="Company Name"
                        value={(data as any)?.gst_verification?.company_name}
                      />
                    </div>
                  )}
                </Section>
              )}

              {/* BANK VERIFICATION */}
              {showMandate && hasBankVerificationMedia && (
                <Section
                  title="BANK VERIFICATION"
                  colorClass="bg-amber-50 text-amber-700"
                >
                  <MediaRow
                    label="UPI Proof"
                    media={findMedia(['upi_file'])}
                    mediaUrl={mediaUrl}
                    setPreviewOpen={setPreviewOpen}
                    setPreviewUrl={setPreviewUrl}
                  />

                  <MediaRow
                    label="Bank Proof"
                    media={findMedia(['bank_verification_file'])}
                    mediaUrl={mediaUrl}
                    setPreviewOpen={setPreviewOpen}
                    setPreviewUrl={setPreviewUrl}
                  />
                </Section>
              )}

              {/* DOC PUSH READINESS */}
              {showDocPush && data?.doc_push_readiness && (
                <Section
                  title="DOC PUSH READINESS"
                  colorClass="bg-violet-50 text-violet-700"
                >
                  <div className="px-4 pt-3 pb-4">
                    {/* Status banner */}
                    <div className="flex items-center gap-3 mb-4">
                      <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold ${
                        data.doc_push_readiness.application_status === 'DOCS_PUSHED'
                          ? 'bg-green-100 text-green-700'
                          : data.doc_push_readiness.application_status === 'PUSH_FAILED'
                          ? 'bg-red-100 text-red-700'
                          : data.doc_push_readiness.application_status === 'DOC_PUSH_REJECTED'
                          ? 'bg-orange-100 text-orange-700'
                          : data.doc_push_readiness.application_status === 'DOC_PUSH_MANUAL_APPROVAL'
                          ? 'bg-yellow-100 text-yellow-700'
                          : 'bg-gray-100 text-gray-600'
                      }`}>
                        {data.doc_push_readiness.application_status ?? 'Not triggered'}
                      </span>
                      <span className={`inline-flex items-center gap-1 text-xs font-medium ${
                        data.doc_push_readiness.doc_push_triggered ? 'text-green-600' : 'text-gray-400'
                      }`}>
                        {data.doc_push_readiness.doc_push_triggered ? '✅ Payload saved' : '⏳ Not yet triggered'}
                      </span>
                    </div>

                    {/* Collapsible groups */}
                    <div className="space-y-2">
                      {data.doc_push_readiness.groups.map((group) => {
                        const isOpen = expandedGroups.has(group.title);
                        const totalItems = group.items.length;
                        const presentItems = group.items.filter(i => i.exists).length;
                        const allPresent = presentItems === totalItems;
                        const nonePresent = presentItems === 0;

                        return (
                          <div
                            key={group.title}
                            className="rounded-xl border border-gray-200 overflow-hidden"
                          >
                            {/* Group header — clickable */}
                            <button
                              type="button"
                              onClick={() => toggleGroup(group.title)}
                              className="w-full flex items-center justify-between px-3 py-2.5 bg-gray-50 hover:bg-gray-100 transition-colors text-left"
                            >
                              <div className="flex items-center gap-2">
                                <span className="text-base">{group.icon}</span>
                                <span className="font-semibold text-sm text-gray-800">{group.title}</span>
                                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                                  allPresent
                                    ? 'bg-green-100 text-green-700'
                                    : nonePresent
                                    ? 'bg-red-100 text-red-600'
                                    : 'bg-yellow-100 text-yellow-700'
                                }`}>
                                  {presentItems}/{totalItems}
                                </span>
                              </div>
                              <span className="text-gray-400 text-xs">{isOpen ? '▲' : '▼'}</span>
                            </button>

                            {/* Group items */}
                            {isOpen && (
                              <div className="divide-y divide-gray-100">
                                {group.items.map((item) => (
                                  <div
                                    key={item.label}
                                    className={`flex items-start justify-between gap-3 px-3 py-2 text-xs ${
                                      item.exists ? 'bg-white' : 'bg-red-50'
                                    }`}
                                  >
                                    <div className="flex items-center gap-2 min-w-0">
                                      <span className="text-sm flex-shrink-0">
                                        {item.exists ? '✅' : '❌'}
                                      </span>
                                      <span className={`font-medium flex-shrink-0 ${item.exists ? 'text-gray-700' : 'text-red-600'}`}>
                                        {item.label}
                                      </span>
                                    </div>
                                    {item.exists && (
                                      <span className="text-gray-500 text-right break-all max-w-[55%]">
                                        {formatReadinessValue(item.value)}
                                      </span>
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </Section>
              )}

              {/* DOC PUSH FILES CHECKLIST */}
              {showDocPush && data?.doc_push_files && data.doc_push_files.length > 0 && (
                <Section
                  title="DOC PUSH FILES"
                  colorClass="bg-indigo-50 text-indigo-700"
                >
                  <div className="px-4 py-3">
                    <p className="text-xs text-gray-500 mb-3">
                      Shows whether each VerificationMedia record has been pre-generated for this user.
                      ✅ = file exists in DB &amp; S3 &nbsp;|&nbsp; ❌ = not yet generated (will be created at doc push time)
                    </p>
                    <div className="grid grid-cols-1 gap-1.5">
                      {data.doc_push_files.map((file) => (
                        <div
                          key={file.key}
                          className={`flex items-center justify-between rounded-lg px-3 py-2 text-sm border ${
                            file.exists
                              ? 'bg-green-50 border-green-200 text-green-800'
                              : 'bg-red-50 border-red-200 text-red-700'
                          }`}
                        >
                          <span className="font-medium">{file.label}</span>
                          <span className="text-base leading-none">
                            {file.exists ? '✅' : '❌'}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </Section>
              )}

              {/* "Not yet reached" notice — shown when user hasn't progressed far enough */}
              {currentPage > 0 && (
                <div className="mx-4 mb-6 mt-2 rounded-xl border border-dashed border-gray-300 bg-gray-50 px-4 py-3">
                  <p className="text-xs text-gray-500 leading-relaxed">
                    <span className="font-semibold text-gray-600">ℹ️ Progressive info:</span>{' '}
                    Sections are shown based on how far the user has progressed.
                    {!showBusiness && ' Business details will appear after page 2.'}
                    {!showBRE && ' BRE/AA info will appear after page 5.'}
                    {!showLoanInfo && ' Loan offer details will appear after page 8.'}
                    {!showBankInfo && ' Bank details will appear after page 13.'}
                    {!showMandate && ' Mandate info will appear after page 14.'}
                    {!showDocPush && ' Doc push details will appear after page 17.'}
                    {showDocPush && ' All sections are now visible.'}
                  </p>
                </div>
              )}
            </>
          )}
        </div>
        <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
          <DialogContent className="max-w-md">
            <img src={previewUrl} className="w-full rounded-lg" />
          </DialogContent>
        </Dialog>
      </DialogContent>
    </Dialog>
  );
};

export default UserDetailModal;